import React from "react";
import { ArrowRight, MapPin, Phone, Building2 } from "lucide-react";
import { locations } from "../data/mock";
import { openContactModal } from "../lib/contactBus";

export default function LocationsPage() {
  return (
    <>
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 pt-16 pb-12">
        <p className="section-tag mb-4">// FIELD OFFICE</p>
        <h1 className="font-display text-[72px] sm:text-[112px] lg:text-[160px] uppercase leading-[0.88]">
          One shop.<br /><span className="text-[var(--terracotta)]">One standard.</span>
        </h1>
        <p className="mt-8 text-[15px] leading-7 text-[var(--slate-700)] max-w-2xl">
          Edge Developer, LLC is headquartered in North Mankato, MN. Our in-house crew serves Mankato, St. Peter, Lake Crystal, Eagle Lake, Madison Lake, and the greater southern Minnesota region — same crew model, same QA standards, same handshake guarantee.
        </p>
      </section>

      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 pb-20">
        <div className="grid grid-cols-12 gap-3 lg:gap-4">
          {locations.map((l) => (
            <div key={l.slug} className="bento-card overflow-hidden group col-span-12 lg:col-span-8">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={l.image} alt={l.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--slate-950)]/60 to-transparent" />
                <span className="absolute top-4 left-4 font-mono-sans text-[10px] bg-[var(--terracotta)] text-[var(--off-white)] px-2 py-1">{l.region}</span>
                <h3 className="absolute bottom-4 left-4 right-4 font-display text-[44px] sm:text-[60px] uppercase text-[var(--off-white)] leading-[0.95]">{l.name}</h3>
              </div>
              <div className="p-8 space-y-5">
                <div>
                  <p className="font-mono-sans text-[10px] text-[var(--terracotta)] mb-1">// SERVICE AREA</p>
                  <p className="text-[14px] text-[var(--slate-700)] leading-7">{l.coverage}</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[var(--slate-300)]">
                  <div>
                    <p className="font-mono-sans text-[10px] text-[var(--terracotta)] mb-1">// ADDRESS</p>
                    <div className="flex items-start gap-2 text-[13px] text-[var(--slate-900)]">
                      <Building2 size={14} className="mt-1 text-[var(--terracotta)] shrink-0" />
                      <span>{l.address}</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-mono-sans text-[10px] text-[var(--terracotta)] mb-1">// PHONE</p>
                    <a href={`tel:${l.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 text-[13px] text-[var(--slate-900)] font-semibold hover:text-[var(--terracotta)] transition-colors">
                      <Phone size={13} /> {l.phone}
                    </a>
                  </div>
                </div>
                <button onClick={openContactModal} className="btn-terracotta mt-2">
                  Schedule A Consultation <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}

          {/* Companion info card */}
          <div className="col-span-12 lg:col-span-4 grid grid-cols-1 gap-3 lg:gap-4">
            <div className="bento-card dark p-6">
              <p className="section-tag !text-[var(--terracotta-light)] mb-3">// HOURS</p>
              <ul className="space-y-2 text-[13.5px] text-[var(--slate-300)]">
                <li className="flex justify-between"><span>Mon – Thu</span><span className="text-[var(--off-white)]">7:00 – 5:00</span></li>
                <li className="flex justify-between"><span>Friday</span><span className="text-[var(--off-white)]">7:00 – 3:00</span></li>
                <li className="flex justify-between"><span>Saturday</span><span className="text-[var(--off-white)]">By appt.</span></li>
                <li className="flex justify-between"><span>Sunday</span><span className="text-[var(--off-white)]">Closed</span></li>
              </ul>
            </div>
            <div className="bento-card terracotta p-6">
              <p className="font-mono-sans text-[10px] mb-3 opacity-80">// DIRECT</p>
              <h4 className="font-display text-[28px] uppercase leading-tight">Email us</h4>
              <a href="mailto:info@edge-developerllc.com" className="text-[13px] mt-2 inline-block break-all underline underline-offset-4">info@edge-developerllc.com</a>
            </div>
            <div className="bento-card p-6">
              <p className="section-tag mb-3">// MAP</p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=743+Centre+North+Mankato+MN+56003"
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-[5/4] bg-[var(--bg-secondary)] border border-[var(--slate-300)] flex items-center justify-center text-center hover:border-[var(--terracotta)] transition-colors"
              >
                <div>
                  <Building2 size={28} className="mx-auto text-[var(--terracotta)] mb-2" strokeWidth={1.5} />
                  <p className="font-mono-sans text-[11px] text-[var(--slate-900)] font-semibold">VIEW ON GOOGLE MAPS</p>
                  <p className="text-[11px] text-[var(--slate-500)] mt-1">743 Centre, North Mankato</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--slate-950)] text-[var(--off-white)] py-20">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-8">
            <p className="section-tag !text-[var(--terracotta-light)] mb-3">// LOCAL FIRST</p>
            <h2 className="font-display text-[48px] sm:text-[72px] uppercase leading-[0.95]">Project nearby? <span className="text-[var(--terracotta)]">Stop in.</span></h2>
          </div>
          <div className="col-span-12 lg:col-span-4 text-right">
            <button onClick={openContactModal} className="btn-terracotta">Book A Visit <ArrowRight size={15} /></button>
          </div>
        </div>
      </section>
    </>
  );
}
